import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Pencil, MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ActivityMap } from '@/components/ActivityMap';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { business_name: '', street_address: '', postal_code: '', city: '', province: '', booking_email: '', contacts: '' };
const ERR_KEYS = { INVALID_POSTAL_CODE: 'act_invalid_postal', INVALID_PROVINCE: 'act_invalid_province', INVALID_EMAIL: 'act_invalid_email' };

const validate = (f) => {
  const e = {};
  Object.keys(EMPTY).forEach(k => { if (!f[k].trim()) e[k] = 'act_required'; });
  if (!e.postal_code && !/^\d{5}$/.test(f.postal_code.trim())) e.postal_code = 'act_invalid_postal';
  if (!e.province && !/^[A-Za-z]{2}$/.test(f.province.trim())) e.province = 'act_invalid_province';
  if (!e.booking_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.booking_email.trim())) e.booking_email = 'act_invalid_email';
  return e;
};

const Field = ({ k, label, form, set, errors, editing, t, className = '', ...rest }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs font-sans text-muted-foreground uppercase tracking-wide">{label} *</Label>
    <Input value={form[k]} onChange={e => set(k, e.target.value)} disabled={!editing} className="rounded-lg h-11" data-testid={`activity-input-${k}`} {...rest} />
    {errors[k] && <p className="text-xs text-destructive font-sans" data-testid={`activity-error-${k}`}>{t(errors[k])}</p>}
  </div>
);

const MapEditor = ({ location, t, onSaved }) => {
  const [pos, setPos] = useState(location);
  const [busy, setBusy] = useState(false);
  useEffect(() => setPos(location), [location]);
  const moved = pos && location && (pos.lat !== location.lat || pos.lng !== location.lng);
  const save = async () => {
    setBusy(true);
    try { await axios.put(`${API}/business/activity/location`, pos, { withCredentials: true }); toast.success(t('act_map_saved')); onSaved(); }
    catch { toast.error('Errore'); } finally { setBusy(false); }
  };
  const hint = !location ? 'act_map_missing' : location.source === 'auto_approx' ? 'act_map_approx' : 'act_map_helper';
  return (
    <div className="space-y-2 sm:col-span-2" data-testid="activity-map-editor">
      <p className="text-xs font-sans text-muted-foreground uppercase tracking-wide flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{t('act_map_title')}</p>
      <ActivityMap location={pos} editable onMove={setPos} height={260} testid="activity-map-edit" />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground font-sans" data-testid="activity-map-hint">{t(hint)}</p>
        <Button size="sm" className="rounded-full" onClick={save} disabled={busy || !pos || (!moved && !!location)} data-testid="activity-map-save-btn">{t('act_map_save')}</Button>
      </div>
    </div>
  );
};

export const ActivityDataCard = ({ t, onChange }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => axios.get(`${API}/business/activity`, { withCredentials: true }).then(r => {
    setData(r.data); setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map(k => [k, r.data[k] || ''])) });
    if (!r.data.is_completed) setEditing(true);
  }).catch(() => setData(false)), []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => { setForm(f => ({ ...f, [k]: k === 'province' ? v.toUpperCase() : v })); setErrors(e => ({ ...e, [k]: undefined })); };
  const save = async () => {
    const e = validate(form); setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      await axios.put(`${API}/business/activity`, form, { withCredentials: true });
      toast.success(t('act_saved')); setEditing(false); await load(); onChange?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.fields) setErrors(Object.fromEntries(d.fields.map(f => [f, ERR_KEYS[d.code] || 'act_required'])));
      else toast.error(typeof d === 'string' ? d : 'Errore');
    } finally { setSaving(false); }
  };
  if (data === null) return null;
  if (data === false) return null;
  const p = { form, set, errors, editing, t };
  return (
    <Card className="border border-primary/30 rounded-2xl mb-6" data-testid="activity-data-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-1">
          <p className="text-sm font-semibold font-sans">{data.is_completed ? t('act_title') : t('act_cta')}</p>
          {!editing ? <Button onClick={() => setEditing(true)} variant="outline" size="sm" className="rounded-full" data-testid="activity-edit-btn"><Pencil className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />{t('act_edit')}</Button> : (
            <div className="flex gap-2">
              {data.is_completed && <Button onClick={() => { setEditing(false); setErrors({}); load(); }} variant="ghost" size="sm" className="rounded-full" data-testid="activity-cancel-btn">{t('act_cancel')}</Button>}
              <Button onClick={save} disabled={saving} size="sm" className="rounded-full" data-testid="activity-save-btn"><Save className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />{saving ? '...' : t('act_save')}</Button>
            </div>)}
        </div>
        <p className="text-xs text-muted-foreground font-sans mb-4">{t('act_helper')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
          <Field k="business_name" label={t('bn_label')} placeholder={t('bn_placeholder')} className="sm:col-span-6" maxLength={120} {...p} />
          <Field k="street_address" label={t('act_street')} placeholder={t('act_street_ph')} className="sm:col-span-6" maxLength={200} {...p} />
          <Field k="postal_code" label={t('act_postal')} placeholder="20121" className="sm:col-span-2" maxLength={5} inputMode="numeric" {...p} />
          <Field k="city" label={t('act_city')} placeholder="Milano" className="sm:col-span-3" maxLength={100} {...p} />
          <Field k="province" label={t('act_province')} placeholder={t('act_province_ph')} className="sm:col-span-1" maxLength={2} {...p} />
          <Field k="booking_email" label={t('act_booking_email')} placeholder="prenotazioni@..." className="sm:col-span-6" type="email" maxLength={200} {...p} />
          <div className="space-y-1.5 sm:col-span-6">
            <Label className="text-xs font-sans text-muted-foreground uppercase tracking-wide">{t('act_contacts')} *</Label>
            <Textarea value={form.contacts} onChange={e => set('contacts', e.target.value)} disabled={!editing} maxLength={300} rows={2} placeholder={t('act_contacts_ph')} className="rounded-lg" data-testid="activity-input-contacts" />
            {errors.contacts && <p className="text-xs text-destructive font-sans" data-testid="activity-error-contacts">{t(errors.contacts)}</p>}
          </div>
          <div className="sm:col-span-6">
            {data.is_completed ? <MapEditor location={data.location} t={t} onSaved={load} /> : <p className="text-xs text-muted-foreground font-sans">{t('act_map_after_save')}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
